import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "info-panel" }
const _hoisted_2 = {
  key: 0,
  class: "sun-times"
}
const _hoisted_3 = { class: "time-row" }
const _hoisted_4 = { class: "time-row" }
const _hoisted_5 = { class: "time-row" }
const _hoisted_6 = {
  key: 1,
  class: "sun-pos"
}
const _hoisted_7 = {
  key: 1,
  class: "hint"
}

import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import L from "leaflet";
import SunCalc from "suncalc";

const RADIUS_METERS = 500; // Radius of the visual circle


export default /*@__PURE__*/_defineComponent({
  __name: 'SunTracker',
  props: {
    map: {},
    date: {},
    active: { type: Boolean }
  },
  emits: ["close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const referenceLocation = ref<L.LatLng | null>(null);
const sunTimes = ref<any>(null);
const sunPos = ref<any>(null);

// Drag State
// Start in the top right corner of the map container
const position = ref({ x: 0, y: 15 });
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

function resetPosition() {
	if (!props.map) return;
	const container = props.map.getContainer();
	const width = container.clientWidth;
	const height = container.clientHeight;

	if (width < 768) {
		// Mobile: Bottom-left
		position.value = { x: 15, y: height - 220 };
	} else {
		// Desktop: Top-left (offset to be below icons)
		position.value = { x: 20, y: 75 };
	}
}

onMounted(() => {
	resetPosition();
});

// Leaflet Layers
const layerGroup = L.layerGroup();
watch(
	() => props.active,
	(newVal) => {
		if (newVal) {
			resetPosition();
			if (props.map) {
				props.map.on("click", onMapClick);
				layerGroup.addTo(props.map);
			}
		} else {
			// When deactivating, fully reset the state
			closeTracker(false);
		}
	},
);

watch(
	() => props.date,
	() => {
		if (props.active && referenceLocation.value) {
			updateVisuals();
		}
	},
);

function closeTracker(emitClose = true) {
	console.log("SunTracker: closing and resetting state");
	if (props.map) {
		props.map.off("click", onMapClick);
		layerGroup.clearLayers();
		layerGroup.remove();
	}
	// Fully reset state to show hint next time
	referenceLocation.value = null;
	sunTimes.value = null;
	sunPos.value = null;

	if (emitClose) emit("close");
}

function onMapClick(e: L.LeafletMouseEvent) {
	referenceLocation.value = e.latlng;
	updateVisuals();
}

// Drag handlers
function startDrag(e: MouseEvent) {
	if ((e.target as HTMLElement).classList.contains("close-btn")) return;

	isDragging.value = true;
	dragOffset.value = {
		x: e.clientX - position.value.x,
		y: e.clientY - position.value.y,
	};

	document.addEventListener("mousemove", onDrag);
	document.addEventListener("mouseup", stopDrag);
}

function onDrag(e: MouseEvent) {
	if (!isDragging.value) return;

	position.value = {
		x: e.clientX - dragOffset.value.x,
		y: e.clientY - dragOffset.value.y,
	};
}

function stopDrag() {
	isDragging.value = false;
	document.removeEventListener("mousemove", onDrag);
	document.removeEventListener("mouseup", stopDrag);
}

function updateVisuals() {
	if (!referenceLocation.value || !props.map) return;

	layerGroup.clearLayers();
	const center = referenceLocation.value;
	const date = props.date || new Date();

	// 1. Calculate Times
	const times = SunCalc.getTimes(date, center.lat, center.lng);
	sunTimes.value = times;

	// 2. Format Labels
	// Draw Horizon Circle
	L.circle(center, {
		radius: RADIUS_METERS,
		color: "#666",
		fill: false,
		weight: 1,
		dashArray: "5, 5",
	}).addTo(layerGroup);

	// 3. Draw Sun Path
	// Sample every 15 mins from sunrise to sunset
	const pathPoints: L.LatLng[] = [];
	let curr = new Date(times.sunrise.getTime() - 30 * 60000); // Start bit before
	const end = new Date(times.sunset.getTime() + 30 * 60000); // End bit after

	while (curr <= end) {
		const pos = SunCalc.getPosition(curr, center.lat, center.lng);
		const point = projectPosition(center, pos.azimuth, pos.altitude);
		if (point) pathPoints.push(point);
		curr = new Date(curr.getTime() + 15 * 60000);
	}

	if (pathPoints.length > 0) {
		L.polyline(pathPoints, { color: "orange", weight: 3 }).addTo(layerGroup);
	}

	// 4. Current Sun Position
	const currentPos = SunCalc.getPosition(date, center.lat, center.lng);
	sunPos.value = currentPos;
	const sunLatLng = projectPosition(
		center,
		currentPos.azimuth,
		currentPos.altitude,
	);

	if (sunLatLng) {
		const sunIcon = L.divIcon({
			className: "sun-icon-marker",
			html: '<div style="font-size: 24px;">☀️</div>',
			iconSize: [30, 30],
			iconAnchor: [15, 15],
		});
		L.marker(sunLatLng, { icon: sunIcon }).addTo(layerGroup);

		// Line from center to sun
		L.polyline([center, sunLatLng], {
			color: "orange",
			weight: 1,
			dashArray: "2,4",
		}).addTo(layerGroup);
	}

	// 5. Sunrise/Sunset Lines
	const sunrisePos = SunCalc.getPosition(times.sunrise, center.lat, center.lng);
	const sunsetPos = SunCalc.getPosition(times.sunset, center.lat, center.lng);

	const sunrisePt = projectPosition(center, sunrisePos.azimuth, 0); // Horizon
	const sunsetPt = projectPosition(center, sunsetPos.azimuth, 0); // Horizon

	if (sunrisePt)
		L.polyline([center, sunrisePt], { color: "blue", weight: 1 }).addTo(
			layerGroup,
		);
	if (sunsetPt)
		L.polyline([center, sunsetPt], { color: "red", weight: 1 }).addTo(
			layerGroup,
		);
}

// Helper: Project Azimuth/Altitude to Map Coordinate offset from Center
function projectPosition(
	center: L.LatLng,
	azimuth: number,
	altitude: number,
): L.LatLng | null {
	// Azimuth: 0 is South, Math.PI * 3/4 is Northwest... SunCalc convention:
	// 0 = South, increases clockwise?? No.
	// SunCalc: azimuth is radians. 0 is South, Math.PI/2 is West. -Math.PI/2 is East.
	// We want to project this onto a circle on the map.
	// Map Angle (Leaflet/North-up): 0 deg (Up) is North.
	// SunCalc 0 (South) -> Map 180 (Down).
	// SunCalc PI/2 (West) -> Map 270 (Left).
	// So MapAngle = Azimuth + Math.PI.

	const mapAngle = azimuth + Math.PI;

	// Distance from center:
	// If we want a "Solar Chart" (Zenith at center), dist = R * (1 - alt / (PI/2))

	let r = RADIUS_METERS * (1 - altitude / (Math.PI / 2));
	// Clamp min r ??

	const R_EARTH = 6378137;
	const dLat = ((r * Math.cos(mapAngle)) / R_EARTH) * (180 / Math.PI);
	const dLng =
		((r * Math.sin(mapAngle)) /
			(R_EARTH * Math.cos((center.lat * Math.PI) / 180))) *
		(180 / Math.PI);

	return L.latLng(center.lat + dLat, center.lng + dLng);
}

function formatTime(d: Date) {
	return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Public Method for Parent
function setLocation(latlng: L.LatLng) {
	referenceLocation.value = latlng;
	updateVisuals();
}

__expose({ setLocation });

onBeforeUnmount(() => {
	closeTracker(false);
});

return (_ctx: any,_cache: any) => {
  return (__props.active)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "sun-tracker-controls",
        style: _normalizeStyle({ top: position.value.y + 'px', left: position.value.x + 'px' })
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", {
            class: "header",
            onMousedown: startDrag
          }, [
            _cache[1] || (_cache[1] = _createElementVNode("h4", null, "☀️ Posizione Sole", -1)),
            _createElementVNode("button", {
              class: "close-btn",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (closeTracker()))
            }, "✕")
          ], 32),
          (referenceLocation.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (sunTimes.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createElementVNode("div", _hoisted_3, [
                        _cache[2] || (_cache[2] = _createElementVNode("span", null, "🌅 Alba:", -1)),
                        _createElementVNode("span", null, _toDisplayString(formatTime(sunTimes.value.sunrise)), 1)
                      ]),
                      _createElementVNode("div", _hoisted_4, [
                        _cache[3] || (_cache[3] = _createElementVNode("span", null, "🏙️ Tramonto:", -1)),
                        _createElementVNode("span", null, _toDisplayString(formatTime(sunTimes.value.sunset)), 1)
                      ]),
                      _createElementVNode("div", _hoisted_5, [
                        _cache[4] || (_cache[4] = _createElementVNode("span", null, "🕛 Mezzogiorno:", -1)),
                        _createElementVNode("span", null, _toDisplayString(formatTime(sunTimes.value.solarNoon)), 1)
                      ])
                    ]))
                  : _createCommentVNode("", true),
                (sunPos.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _createElementVNode("div", null, " Azimuth: " + _toDisplayString(Math.round((sunPos.value.azimuth * 180) / Math.PI + 180)) + "° ", 1),
                      _createElementVNode("div", null, " Altitudine: " + _toDisplayString(Math.round((sunPos.value.altitude * 180) / Math.PI)) + "° ", 1)
                    ]))
                  : _createCommentVNode("", true)
              ], 64))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, "Clicca sulla mappa per posizionare"))
        ])
      ], 4))
    : _createCommentVNode("", true)
}
}

})